import { MapPin, Briefcase, CheckCircle, Gift, ExternalLink } from "lucide-react";

const SECTION_ICON_SIZE = 22;

const responsibilities = [
  { id: "guest-relations", label: "Guest Relations", desc: "Handle flexible check-ins, check-outs, emergency requests, and local guiding." },
  { id: "operations", label: "Operations", desc: "Work with the hospitality department to ensure proper housekeeping and laundry services, and kitchen staff to ensure efficient food service." },
  { id: "facility-upkeep", label: "Facility Upkeep", desc: "Ensure the proper management of routine maintenance, solar/generator power systems, and water supply in collaboration with the Operations Manager." },
  { id: "inventory", label: "Inventory & Sourcing", desc: "Work with hospitality and catering services to facilitate purchase of fresh local ingredients, linens, and hospitality supplies." },
  { id: "financials", label: "Financials", desc: "Manage the daily budget, track expenses, and process guest payments." },
  { id: "on-call", label: "On-Call Availability", desc: "Serve as the primary on-site contact for guest needs outside standard hours." },
  { id: "marketing", label: "Marketing", desc: "Work with the Marketing Manager to ensure continued visibility of The Bridge Lodge in the local communities and beyond, through social media, events and campaigns." },
];

const qualifications = [
  { id: "experience", label: "Experience", desc: "No hospitality experience is required, but a flexible and willing-to-learn attitude is a must." },
  { id: "flexibility", label: "Flexibility", desc: "Willingness to work irregular, non-traditional hours based on guest occupancy." },
  { id: "skills", label: "Skills", desc: "Strong problem-solving abilities and basic property maintenance knowledge." },
  { id: "tech", label: "Tech", desc: "Proficiency with WhatsApp, email, and basic booking spreadsheet software." },
  { id: "traits", label: "Traits", desc: "Excellent communication, resourcefulness, and comfort living in a rural area." },
];

const benefits = [
  { id: "accommodation", label: "Accommodation", desc: "Private on-site living quarters provided." },
  { id: "utilities", label: "Utilities", desc: "Complimentary solar energy, water, and internet access included." },
];

function JobHero() {
  return (
    <section className="bg-[#C8C5BC]/30 pt-28 sm:pt-36 pb-16 sm:pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4 animate-fade-in-up opacity-0">
          Work With Us
        </p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#1C2333] leading-tight animate-fade-in-up opacity-0 animate-delay-100">
          The Bridge Lodge Manager
        </h1>
        <p className="mt-4 font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#1B3D2F] font-light animate-fade-in-up opacity-0 animate-delay-200">
          Full-Time, Live-In
        </p>
        <div className="mt-6 flex items-center justify-center gap-2 text-[#1C2333]/70 animate-fade-in-up opacity-0 animate-delay-300">
          <MapPin size={18} className="text-[#C4883A]" />
          <span className="text-base sm:text-lg">The Bridge Lodge, Abule Imole</span>
        </div>
      </div>
    </section>
  );
}

function JobOverview() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <Briefcase size={SECTION_ICON_SIZE} className="text-[#C4883A]" />
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1B3D2F]">Job Overview</h2>
        </div>
        <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80">
          The Bridge Lodge is looking for a dedicated, live-in Guest House Manager. You will manage daily operations, guest services, and property upkeep while residing on-site. This full-time role requires high schedule flexibility to handle evening arrivals or morning requests. It is perfect for a self-motivated leader who enjoys rural community living.
        </p>
      </div>
    </section>
  );
}

function ResponsibilitiesSection() {
  return (
    <section className="py-16 sm:py-24 bg-[#D0D5C8]/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1B3D2F] mb-10">Key Responsibilities</h2>
        <div className="flex flex-col gap-6">
          {responsibilities.map((item) => (
            <div key={item.id} className="flex gap-4 items-start">
              <div className="w-2 h-2 mt-3 bg-[#C4883A] shrink-0" />
              <div>
                <span className="font-semibold text-[#1C2333] text-lg">{item.label}:</span>
                <span className="text-lg leading-relaxed text-[#1C2333]/80 ml-1">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QualificationsSection() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-10">
          <CheckCircle size={SECTION_ICON_SIZE} className="text-[#C4883A]" />
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1B3D2F]">Required Qualifications</h2>
        </div>
        <div className="flex flex-col gap-6">
          {qualifications.map((item) => (
            <div key={item.id} className="flex gap-4 items-start">
              <div className="w-2 h-2 mt-3 bg-[#1B3D2F] shrink-0" />
              <div>
                <span className="font-semibold text-[#1C2333] text-lg">{item.label}:</span>
                <span className="text-lg leading-relaxed text-[#1C2333]/80 ml-1">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  return (
    <section className="py-16 sm:py-24 bg-[#D0D5C8]/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-10">
          <Gift size={SECTION_ICON_SIZE} className="text-[#C4883A]" />
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1B3D2F]">Benefits</h2>
        </div>
        <div className="flex flex-col gap-6">
          {benefits.map((item) => (
            <div key={item.id} className="flex gap-4 items-start">
              <div className="w-2 h-2 mt-3 bg-[#A3B18A] shrink-0" />
              <div>
                <span className="font-semibold text-[#1C2333] text-lg">{item.label}:</span>
                <span className="text-lg leading-relaxed text-[#1C2333]/80 ml-1">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ApplySection() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1B3D2F] mb-6">How to Apply</h2>
        <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80 mb-10">
          Please click the link below to complete a brief application form:
        </p>
        <a
          href="https://tally.so/r/Bzlk41"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="apply-btn"
          className="bg-[#C4883A] text-white hover:bg-[#A8722E] px-10 py-5 text-lg sm:text-xl font-medium transition-all duration-300 inline-flex items-center gap-2"
        >
          Apply Now <ExternalLink size={20} />
        </a>
      </div>
    </section>
  );
}

export default function WorkWithUs() {
  return (
    <div data-testid="work-with-us-page" className="min-h-screen flex flex-col">
      {/* Minimal header with logo only */}
      <nav className="bg-white border-b border-[#C8C5BC]/30 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <a href="/" data-testid="work-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_abule-imole/artifacts/6soepudp_Cooperative.png"
              alt="Abule Imole Village of Light"
              className="h-14 sm:h-[7.5rem] w-auto"
            />
          </a>
        </div>
      </nav>

      <main className="flex-1">
        <JobHero />
        <JobOverview />
        <ResponsibilitiesSection />
        <QualificationsSection />
        <BenefitsSection />
        <ApplySection />
      </main>

      {/* Minimal footer */}
      <footer className="bg-[#1B3D2F] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Abule Imole Village of Light. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
